import React, { useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useAuthStore } from "../store/authStore";
import { useNotificationsStore } from "../store/notificationsStore";
import Logo from "./Logo";
import NotificationsModal from "./NotificationsModal";

const SidebarContainer = styled.div<{ $isVisible?: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: #081c21;
  width: 240px;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    z-index: 100;
    transform: translateX(${(props) => (props.$isVisible ? "0" : "-100%")});
    transition: transform 0.3s ease;
    box-shadow: ${(props) =>
    props.$isVisible ? "0 0 10px rgba(0, 0, 0, 0.5)" : "none"};
  }
`;

const MobileCloseButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 8px;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 16px 16px;

  @media (max-width: 768px) {
    padding-top: 50px;
  }
`;

const Divider = styled.div`
  height: 1px;
  width: calc(100% + 1px);
  background-color: rgb(79 79 79 / 20%);
  margin: 0 0 8px;
  position: relative;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const NavSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const BottomSection = styled.div`
  padding: 16px;
  margin-top: auto;
`;

// Оставляем для поддержки совместимости с другими местами, где может использоваться
const SidebarSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 16px;
`;

const LogoLink = styled.a`
  display: flex;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const NavMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  text-decoration: none;
  color: #8ca9a9;
  background-color: transparent;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.04);
  }

  &.active {
    color: #ffffff;
    background-color: rgba(36, 185, 125, 0.1);
    border-right: 2px solid #24b97d;
    border-left: none;

    ${IconWrapper} {
      color: #24ba7d;
    }
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.04);
  }
`;

const NotificationButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: rgba(255, 255, 255, 0.04);
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 16px;
  background-color: #FF6B6B;
  color: white;
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavItemText = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  flex-grow: 1;
`;

// Иконки для меню
const ProfileIcon = ({ isActive }: { isActive?: boolean }) => (
  <svg
    width="14"
    height="19"
    viewBox="0 0 14 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.14453 10.2393C8.7695 10.2393 10.4326 10.6472 11.7236 11.5088C12.8885 12.2838 13.5713 13.3817 13.5713 14.5762C13.5711 15.7699 12.8899 16.8686 11.7266 17.6475C10.4316 18.5139 8.76516 18.9248 7.13867 18.9248C5.51253 18.9248 3.84666 18.5145 2.55176 17.6484C1.38722 16.8735 0.705103 15.7763 0.705078 14.582C0.705078 13.388 1.3871 12.2888 2.55078 11.5098L2.55273 11.5078C3.85113 10.6469 5.51863 10.2393 7.14453 10.2393ZM7.14453 11.7393C5.74551 11.7393 4.38337 12.0938 3.38184 12.7578C2.5309 13.3284 2.20508 14.0038 2.20508 14.582C2.2051 15.1243 2.49176 15.75 3.22949 16.293L3.38281 16.4004L3.38477 16.4014C4.38137 17.0682 5.7403 17.4248 7.13867 17.4248C8.53681 17.4248 9.89508 17.068 10.8916 16.4014C11.7444 15.8305 12.0711 15.155 12.0713 14.5762C12.0713 13.9978 11.7453 13.3245 10.8936 12.7578L10.8926 12.7568C9.90048 12.0942 8.54382 11.7393 7.14453 11.7393Z"
      fill={isActive ? "#24B97D" : "#8CA9A9"}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.00098 0.916992C9.45677 0.917098 11.4512 2.91137 11.4512 5.36719V5.37012C11.4412 7.77051 9.54827 9.72829 7.15918 9.80859C7.12594 9.8097 7.09267 9.80799 7.05957 9.80469C7.02801 9.80153 6.97561 9.80122 6.92676 9.80566C6.89592 9.80846 6.86493 9.80963 6.83398 9.80859C4.44551 9.72831 2.55088 7.77047 2.55078 5.36719C2.55078 2.91241 4.53566 0.916992 7.00098 0.916992ZM7.00098 2.41699C5.36629 2.41699 4.05078 3.73863 4.05078 5.36719C4.05088 6.95826 5.29771 8.24632 6.86816 8.30762C6.95168 8.30292 7.03811 8.30299 7.12207 8.30762C8.69237 8.24789 9.94281 6.96081 9.95117 5.36719L9.94727 5.21582C9.8684 3.65848 8.57765 2.41709 7.00098 2.41699Z"
      fill={isActive ? "#24B97D" : "#8CA9A9"}
    />
  </svg>
);

const StatsIcon = ({ isActive }: { isActive?: boolean }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M16.5 16.7083L16.626 16.721C16.9108 16.7793 17.125 17.0313 17.125 17.3333C17.125 17.6353 16.9108 17.8873 16.626 17.9456L16.5 17.9583H1.5C1.15482 17.9583 0.875 17.6785 0.875 17.3333C0.875 16.9881 1.15482 16.7083 1.5 16.7083H16.5Z"
      fill={isActive ? "#24B97D" : "#609F94"}
    />
    <path
      d="M3.875 6.81623C3.87482 6.70334 3.77989 6.60842 3.66699 6.60823H2.33301C2.2201 6.60841 2.12518 6.70333 2.125 6.81623V13.9998C2.125 14.1129 2.22 14.2077 2.33301 14.2078H3.66699C3.78 14.2076 3.875 14.1129 3.875 13.9998V6.81623ZM5.125 13.9998C5.125 14.8032 4.47035 15.4576 3.66699 15.4578H2.33301C1.52965 15.4577 0.875 14.8032 0.875 13.9998V6.81623C0.875177 6.01298 1.52975 5.3584 2.33301 5.35823H3.66699C4.47024 5.35841 5.12482 6.01299 5.125 6.81623V13.9998Z"
      fill={isActive ? "#24B97D" : "#609F94"}
    />
    <path
      d="M9.87435 4.15836C9.87417 4.04546 9.77924 3.95054 9.66634 3.95035H8.33236C8.21945 3.95053 8.12453 4.04545 8.12435 4.15836V14.0002C8.12435 14.1132 8.21934 14.209 8.33236 14.2091H9.66634C9.77934 14.2089 9.87435 14.1132 9.87435 14.0002V4.15836ZM11.1243 14.0002C11.1243 14.8035 10.4697 15.4589 9.66634 15.4591H8.33236C7.52899 15.459 6.87435 14.8036 6.87435 14.0002V4.15836C6.87453 3.3551 7.5291 2.70052 8.33236 2.70035H9.66634C10.4696 2.70054 11.1242 3.35511 11.1243 4.15836V14.0002Z"
      fill={isActive ? "#24B97D" : "#609F94"}
    />
    <path
      d="M15.8753 1.49966C15.8751 1.38677 15.7802 1.29185 15.6673 1.29166H14.3333C14.2204 1.29184 14.1255 1.38676 14.1253 1.49966V13.9997C14.1253 14.1127 14.2203 14.2085 14.3333 14.2086H15.6673C15.7803 14.2085 15.8753 14.1127 15.8753 13.9997V1.49966ZM17.1253 13.9997C17.1253 14.8031 16.4707 15.4585 15.6673 15.4586H14.3333C13.53 15.4585 12.8753 14.8031 12.8753 13.9997V1.49966C12.8755 0.69641 13.5301 0.0418333 14.3333 0.0416565H15.6673C16.4706 0.0418443 17.1251 0.696417 17.1253 1.49966V13.9997Z"
      fill={isActive ? "#24B97D" : "#609F94"}
    />
  </svg>
);

const InventoryIcon = ({ isActive }: { isActive?: boolean }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.00195 0.0419922C9.70538 0.0420499 10.4438 0.189754 11.0381 0.520508L15.4854 2.99512C16.1103 3.34115 16.6429 3.92297 17.0166 4.55664C17.3903 5.19038 17.6426 5.93701 17.6426 6.65039V11.3584C17.6426 12.0718 17.3903 12.8184 17.0166 13.4521C16.6429 14.0858 16.1112 14.6666 15.4863 15.0127L15.4873 15.0137L11.0381 17.4883L10.8076 17.6035C10.261 17.8499 9.61926 17.9609 9.00488 17.9609C8.39057 17.9609 7.74878 17.8499 7.20215 17.6035L6.97168 17.4883L2.52148 15.0137C1.89707 14.6675 1.36562 14.0855 0.992188 13.4521C0.618533 12.8184 0.367188 12.0717 0.367188 11.3584V6.65039C0.367188 5.93701 0.618504 5.19038 0.992188 4.55664C1.36587 3.92293 1.89753 3.34121 2.52246 2.99512L6.97168 0.520508C7.56283 0.189186 8.29941 0.0419922 9.00195 0.0419922ZM13.791 7.40332V10.0322C13.791 10.3773 13.5111 10.6572 13.166 10.6572C12.8209 10.6572 12.5411 10.3773 12.541 10.0322V8.12793L9.625 9.81738V16.6582C9.93397 16.6056 10.2123 16.5158 10.4316 16.3945L14.8799 13.9209L14.8809 13.9199C15.2642 13.7077 15.6496 13.3107 15.9404 12.8174C16.2313 12.324 16.3926 11.795 16.3926 11.3584V6.65039C16.3926 6.43776 16.3531 6.20358 16.2803 5.96191L13.791 7.40332ZM1.72559 5.97168C1.6547 6.2098 1.61719 6.4407 1.61719 6.65039V11.3584C1.61719 11.795 1.77844 12.324 2.06934 12.8174C2.36008 13.3104 2.74481 13.7077 3.12793 13.9199L3.12891 13.9209L7.57715 16.3945C7.79432 16.5145 8.06955 16.6034 8.375 16.6562V9.81836L1.72559 5.97168ZM3.12891 4.08789L3.12793 4.08887C2.83645 4.25034 2.54422 4.5191 2.29297 4.85547L8.99805 8.73633L11.9697 7.01367L5.05176 3.01855L3.12891 4.08789ZM9.00195 1.29199C8.45315 1.29199 7.93959 1.41102 7.58105 1.6123L7.5791 1.61328L6.32422 2.31055L13.2178 6.29102L15.7109 4.84766C15.4607 4.51484 15.1702 4.2491 14.8809 4.08887L14.8799 4.08789L10.4297 1.61328C10.0659 1.41085 9.55028 1.29205 9.00195 1.29199Z"
      fill={isActive ? "#24B97D" : "#609F94"}
    />
  </svg>
);

const ShipmentsIcon = ({ isActive }: { isActive?: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
    <g clipPath="url(#clip0_4418_9504)">
      <path d="M15 2V12C15 13.1 14.1 14 13 14H2V7.62C2.73 8.49 3.85003 9.03 5.09003 9C6.10003 8.98 7.01 8.59 7.69 7.94C8 7.68 8.26002 7.34999 8.46002 6.98999C8.82002 6.37999 9.02 5.65997 9 4.90997C8.97 3.73997 8.45001 2.71 7.64001 2H15Z" stroke={isActive ? "#24B97D" : "#609F94"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M22 14V17C22 18.66 20.66 20 19 20H18C18 18.9 17.1 18 16 18C14.9 18 14 18.9 14 20H10C10 18.9 9.1 18 8 18C6.9 18 6 18.9 6 20H5C3.34 20 2 18.66 2 17V14H13C14.1 14 15 13.1 15 12V5H16.84C17.56 5 18.22 5.39001 18.58 6.01001L20.29 9H19C18.45 9 18 9.45 18 10V13C18 13.55 18.45 14 19 14H22Z" stroke={isActive ? "#24B97D" : "#609F94"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 22C9.10457 22 10 21.1046 10 20C10 18.8954 9.10457 18 8 18C6.89543 18 6 18.8954 6 20C6 21.1046 6.89543 22 8 22Z" stroke={isActive ? "#24B97D" : "#609F94"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 22C17.1046 22 18 21.1046 18 20C18 18.8954 17.1046 18 16 18C14.8954 18 14 18.8954 14 20C14 21.1046 14.8954 22 16 22Z" stroke={isActive ? "#24B97D" : "#609F94"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M22 12V14H19C18.45 14 18 13.55 18 13V10C18 9.45 18.45 9 19 9H20.29L22 12Z" stroke={isActive ? "#24B97D" : "#609F94"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 5C9 6.2 8.47001 7.27 7.64001 8C6.93001 8.62 6.01 9 5 9C2.79 9 1 7.21 1 5C1 3.74 1.58 2.61 2.5 1.88C3.19 1.33 4.06 1 5 1C7.21 1 9 2.79 9 5Z" stroke={isActive ? "#24B97D" : "#609F94"} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.25 3.75V5.25L4 6" stroke={isActive ? "#24B97D" : "#609F94"} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="clip0_4418_9504">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const SalesIcon = ({ isActive }: { isActive?: boolean }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g transform="scale(0.75)">
      <path
        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
        stroke={isActive ? "#24B97D" : "#609F94"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 15L15 9"
        stroke={isActive ? "#24B97D" : "#609F94"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.4945 14.5H14.5035"
        stroke={isActive ? "#24B97D" : "#609F94"}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.49451 9.5H9.50349"
        stroke={isActive ? "#24B97D" : "#609F94"}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

const SupplyIcon = ({ isActive }: { isActive?: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.333 11.9997C18.6088 12 18.8328 12.2239 18.833 12.4997C18.833 16.0007 16.0009 18.8324 12.5 18.8327C12.3199 18.8327 12.1532 18.7365 12.0645 18.5798C11.9757 18.423 11.9786 18.2303 12.0713 18.0759L12.9463 16.6179C13.0884 16.3815 13.3952 16.3042 13.6318 16.446C13.8682 16.588 13.9451 16.8949 13.8037 17.1315L13.4326 17.7497C15.9352 17.3091 17.833 15.13 17.833 12.4997C17.8332 12.2238 18.0571 11.9999 18.333 11.9997Z"
      fill={isActive ? "#24B97D" : "#609F94"}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.2666 10.3337C5.56576 10.3337 5.88671 10.3865 6.16797 10.5104L6.28613 10.5681L6.29199 10.571L8.29199 11.6794C8.61918 11.8586 8.8837 12.1544 9.06543 12.4645C9.24747 12.7754 9.37402 13.1499 9.37402 13.5173V15.6335C9.37394 16.0033 9.24408 16.3788 9.06152 16.6882C8.87891 16.9975 8.61371 17.2917 8.29199 17.4704L6.29199 18.5798L6.29102 18.5788C5.98249 18.7504 5.61064 18.8229 5.2666 18.8229C4.92444 18.8229 4.55472 18.7511 4.24902 18.5788L2.24902 17.4714C1.92215 17.2922 1.65723 16.9961 1.47559 16.6862C1.2934 16.3753 1.1661 16.001 1.16602 15.6335V13.5173C1.16602 13.1474 1.29696 12.772 1.47949 12.4626C1.66202 12.1534 1.92651 11.8591 2.24805 11.6804L2.24902 11.6794L4.24902 10.571L4.25 10.57C4.55517 10.4025 4.92513 10.3337 5.2666 10.3337ZM5.76562 15.0798V17.7233C5.77926 17.7169 5.79336 17.7126 5.80566 17.7057L5.80664 17.7048L7.80664 16.5964L7.90527 16.529C8.00638 16.4484 8.1122 16.3284 8.2002 16.1794C8.3174 15.9807 8.37393 15.7801 8.37402 15.6335V13.5671L5.76562 15.0798ZM2.16602 15.6335C2.16611 15.7824 2.22233 15.9842 2.33789 16.1813C2.42469 16.3293 2.52938 16.448 2.63086 16.528L2.7334 16.5964L4.7334 17.7048L4.73828 17.7077C4.74666 17.7125 4.75654 17.7149 4.76562 17.7194V15.0788L2.16602 13.5729V15.6335ZM5.2666 11.3337C5.04381 11.3337 4.85106 11.3813 4.73145 11.447L2.7334 12.5544C2.68663 12.5804 2.63837 12.6177 2.58887 12.6628L5.26562 14.2136L7.94824 12.6579C7.93476 12.646 7.92261 12.6333 7.90918 12.6227L7.80664 12.5544L5.80957 11.447C5.68347 11.38 5.48737 11.3337 5.2666 11.3337Z"
      fill={isActive ? "#24B97D" : "#609F94"}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.7324 1.16669C15.0312 1.16676 15.352 1.21988 15.6318 1.3454L15.749 1.40302L15.751 1.40399L17.751 2.51239C18.0781 2.69161 18.3427 2.98753 18.5244 3.29755C18.7065 3.60841 18.833 3.98284 18.833 4.35028V6.46649C18.833 6.83637 18.7031 7.21179 18.5205 7.52118C18.338 7.83045 18.0726 8.1246 17.751 8.30341L15.751 9.41278L15.75 9.4118C15.4416 9.58337 15.0695 9.65588 14.7256 9.65594C14.3835 9.65588 14.0137 9.58405 13.708 9.4118L11.708 8.30438C11.3811 8.12519 11.1162 7.8291 10.9346 7.51923C10.7523 7.20825 10.625 6.83416 10.625 6.46649V4.35028C10.625 3.98032 10.7559 3.60502 10.9385 3.29559C11.121 2.98645 11.3855 2.69209 11.707 2.51337L11.708 2.51239L13.708 1.40399L13.7129 1.40106C14.0201 1.23576 14.3905 1.16669 14.7324 1.16669ZM11.625 6.46649C11.625 6.61547 11.6812 6.817 11.7969 7.01434C11.8837 7.16232 11.9884 7.28102 12.0898 7.36102L12.1924 7.42938L14.1924 8.53778L14.1973 8.54071C14.2083 8.54696 14.2212 8.55044 14.2334 8.55634V5.91376L11.625 4.40594V6.46649ZM15.2334 5.9118V8.55243C15.2437 8.54738 15.2551 8.54407 15.2646 8.53876L15.2656 8.53778L17.2656 7.42938L17.3643 7.362C17.4653 7.28131 17.5712 7.1614 17.6592 7.01239C17.7764 6.81362 17.833 6.61309 17.833 6.46649V4.40106L15.2334 5.9118ZM14.7324 2.16669C14.5104 2.16669 14.3135 2.21429 14.1875 2.28192L12.1924 3.38739C12.1455 3.41347 12.0965 3.45061 12.0469 3.49579L14.7314 5.04657L17.4072 3.49188C17.3936 3.47984 17.3817 3.46643 17.3682 3.45575L17.2656 3.38739L15.2676 2.27997C15.148 2.21434 14.9551 2.16678 14.7324 2.16669Z"
      fill={isActive ? "#24B97D" : "#609F94"}
    />
    <path
      d="M7.49902 1.16669C7.67916 1.16669 7.84582 1.26384 7.93457 1.42059C8.02315 1.57725 8.02028 1.76916 7.92773 1.92352L7.05273 3.38251L6.62402 3.12469L6.19531 2.86786L6.56543 2.24872C4.06316 2.68946 2.16617 4.86969 2.16602 7.49969C2.16602 7.77584 1.94216 7.99969 1.66602 7.99969C1.38987 7.99969 1.16602 7.77584 1.16602 7.49969C1.16619 3.99881 3.99814 1.16686 7.49902 1.16669Z"
      fill={isActive ? "#24B97D" : "#609F94"}
    />
    <path
      d="M7.05273 3.38251C6.91057 3.61904 6.60387 3.69541 6.36719 3.55341C6.13053 3.41134 6.05341 3.10459 6.19531 2.86786L7.05273 3.38251Z"
      fill={isActive ? "#24B97D" : "#609F94"}
    />
  </svg>
);

const SearchIcon = ({ isActive }: { isActive?: boolean }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_4418_7059)">
      <path
        d="M11.9998 13.3008C11.8698 13.3008 11.7398 13.2709 11.6198 13.2009L2.78985 8.09088C2.42985 7.88088 2.30983 7.42085 2.51983 7.06085C2.72983 6.70085 3.17983 6.58083 3.54983 6.79083L11.9998 11.6808L20.3998 6.82086C20.7598 6.61086 21.2198 6.74088 21.4298 7.09088C21.6398 7.45088 21.5098 7.91085 21.1598 8.12085L12.3899 13.2009C12.2599 13.2609 12.1298 13.3008 11.9998 13.3008Z"
        fill={isActive ? "#24B97D" : "#609F94"}
      />
      <path
        d="M12 22.3591C11.59 22.3591 11.25 22.0191 11.25 21.6091V12.5391C11.25 12.1291 11.59 11.7891 12 11.7891C12.41 11.7891 12.75 12.1291 12.75 12.5391V21.6091C12.75 22.0191 12.41 22.3591 12 22.3591Z"
        fill={isActive ? "#24B97D" : "#609F94"}
      />
      <path
        d="M11.9999 22.7502C11.0899 22.7502 10.2199 22.5402 9.55988 22.1702L4.21988 19.2103C2.76988 18.4103 1.62988 16.4802 1.62988 14.8202V9.16021C1.62988 7.50021 2.76988 5.58026 4.21988 4.77026L9.55988 1.81023C10.9199 1.05023 13.0599 1.05023 14.4299 1.81023L19.7699 4.77026C21.2199 5.57026 22.3599 7.50021 22.3599 9.16021V12.8202C22.3599 13.2302 22.0199 13.5702 21.6099 13.5702C21.1999 13.5702 20.8599 13.2302 20.8599 12.8202V9.16021C20.8599 8.06021 20.0099 6.62026 19.0499 6.09026L13.7099 3.13024C12.7999 2.62024 11.2099 2.62024 10.2999 3.13024L4.95987 6.09026C3.98987 6.63026 3.14987 8.06021 3.14987 9.16021V14.8202C3.14987 15.9202 3.99987 17.3603 4.95987 17.8903L10.2999 20.8502C11.1799 21.3402 12.8399 21.3402 13.7099 20.8502C14.0699 20.6502 14.5299 20.7803 14.7299 21.1403C14.9299 21.5003 14.7999 21.9602 14.4399 22.1602C13.7799 22.5402 12.9099 22.7502 11.9999 22.7502Z"
        fill={isActive ? "#24B97D" : "#609F94"}
      />
      <path
        d="M19.2 22.15C17.02 22.15 15.25 20.38 15.25 18.2C15.25 16.02 17.02 14.25 19.2 14.25C21.38 14.25 23.15 16.02 23.15 18.2C23.15 20.38 21.38 22.15 19.2 22.15ZM19.2 15.75C17.85 15.75 16.75 16.85 16.75 18.2C16.75 19.55 17.85 20.65 19.2 20.65C20.55 20.65 21.65 19.55 21.65 18.2C21.65 16.85 20.55 15.75 19.2 15.75Z"
        fill={isActive ? "#24B97D" : "#609F94"}
      />
      <path
        d="M22.9999 22.7495C22.8099 22.7495 22.6199 22.6795 22.4699 22.5295L21.4699 21.5295C21.1799 21.2395 21.1799 20.7595 21.4699 20.4695C21.7599 20.1795 22.2399 20.1795 22.5299 20.4695L23.5299 21.4695C23.8199 21.7595 23.8199 22.2395 23.5299 22.5295C23.3799 22.6795 23.1899 22.7495 22.9999 22.7495Z"
        fill={isActive ? "#24B97D" : "#609F94"}
      />
    </g>
    <defs>
      <clipPath id="clip0_4418_7059">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const PlansIcon = ({ isActive }: { isActive?: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.1904 0.916992C16.3859 0.916992 17.4184 1.1528 18.1328 1.86719C18.847 2.58155 19.082 3.6133 19.082 4.80859V7.5C19.082 8.69548 18.8472 9.728 18.1328 10.4424C17.4264 11.1488 16.409 11.3844 15.2305 11.3896C15.2242 12.5648 14.988 13.5793 14.2832 14.2842C13.5784 14.989 12.5638 15.2252 11.3887 15.2314C11.3834 16.4099 11.1478 17.4274 10.4414 18.1338C9.72702 18.8482 8.6945 19.083 7.49902 19.083H4.80762C3.61233 19.083 2.58058 18.8479 1.86621 18.1338C1.24107 17.5087 0.98255 16.6401 0.927734 15.6318L0.916016 15.1914V12.5C0.916023 11.3045 1.15183 10.273 1.86621 9.55859C2.57084 8.85397 3.58408 8.61577 4.75879 8.60938C4.76417 7.43135 5.00182 6.41518 5.70801 5.70898C6.4142 5.00279 7.43037 4.76515 8.6084 4.75977C8.6148 3.58506 8.85299 2.57182 9.55762 1.86719C10.272 1.15281 11.3036 0.917001 12.499 0.916992H15.1904ZM4.75781 10.1094C3.7443 10.1161 3.21804 10.3279 2.92676 10.6191C2.63074 10.9152 2.41602 11.4539 2.41602 12.5V15.1914L2.42578 15.5605C2.47086 16.3688 2.66771 16.8142 2.92676 17.0732C3.22284 17.3691 3.76182 17.583 4.80762 17.583H7.49902C8.5452 17.583 9.08483 17.3693 9.38086 17.0732C9.67293 16.7812 9.88402 16.2523 9.88965 15.2334H8.64941C7.45412 15.2334 6.42237 14.9983 5.70801 14.2842C4.99362 13.5698 4.75781 12.5373 4.75781 11.3418V10.1094ZM8.64941 6.25879C7.60328 6.2588 7.06458 6.47351 6.76855 6.76953C6.47253 7.06556 6.25782 7.60426 6.25781 8.65039V9.27734C6.2607 9.30409 6.26562 9.33088 6.26562 9.3584V11.3418C6.26564 12.3877 6.48044 12.9266 6.77637 13.2227C7.07235 13.5186 7.61134 13.7333 8.65723 13.7334H11.3408C12.387 13.7334 12.9266 13.5197 13.2227 13.2236C13.5187 12.9276 13.7324 12.388 13.7324 11.3418V8.65039C13.7324 8.52248 13.7288 8.40222 13.7227 8.28906C13.6775 7.48131 13.4806 7.03631 13.2217 6.77734C12.9256 6.48142 12.3867 6.26661 11.3408 6.2666H9.35742C9.32293 6.2666 9.28911 6.2633 9.25586 6.25879H8.64941ZM12.499 2.41699C11.4529 2.417 10.9142 2.63171 10.6182 2.92773C10.3269 3.21902 10.1151 3.74528 10.1084 4.75879H11.3438L11.7852 4.77148C12.7942 4.82929 13.6621 5.08788 14.2861 5.71191C15 6.42585 15.2324 7.45641 15.2324 8.65039V9.88965C16.2513 9.88401 16.7802 9.6739 17.0723 9.38184C17.3683 9.08581 17.582 8.54618 17.582 7.5V4.80859C17.582 3.76279 17.3681 3.22382 17.0723 2.92773C16.7762 2.63171 16.2366 2.41699 15.1904 2.41699H12.499Z"
      fill={isActive ? "#24B97D" : "#609F94"}
    />
  </svg>
);

const LogoutIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.41669 6.3C7.67502 3.3 9.21669 2.07501 12.5917 2.07501H12.7C16.4167 2.07501 17.9167 3.57501 17.9167 7.29167V12.725C17.9167 16.4417 16.4167 17.9417 12.7 17.9417H12.5917C9.24169 17.9417 7.69169 16.7334 7.42502 13.7834"
      stroke="#D85355"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.5 10H3.01666"
      stroke="#D85355"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.87498 7.20834L2.08331 10L4.87498 12.7917"
      stroke="#D85355"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NotificationIcon = ({ hasUnread }: { hasUnread?: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 8A6 6 0 0 0 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z"
      stroke={hasUnread ? "#24B97D" : "#609F94"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21"
      stroke={hasUnread ? "#24B97D" : "#609F94"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {hasUnread && (
      <circle
        cx="19"
        cy="6"
        r="3"
        fill="#FF6B6B"
        stroke="#081c21"
        strokeWidth="1"
      />
    )}
  </svg>
);

interface SidebarProps {
  activePage?: string;
  isVisible?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  activePage,
  isVisible = true,
  onClose,
}) => {
  const { logout } = useAuthStore();
  const { 
    unreadCount, 
    isModalOpen, 
    openModal, 
    closeModal, 
    fetchUnreadNotifications 
  } = useNotificationsStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Определяем активную страницу на основе текущего пути
  const getActivePageFromPath = (pathname: string): string => {
    if (pathname.startsWith("/profile")) return "profile";
    if (pathname.startsWith("/statistics")) return "statistics";
    if (pathname.startsWith("/search")) return "search";
    if (pathname.startsWith("/inventory")) return "inventory";
    if (pathname.startsWith("/sales")) return "sales";
    if (pathname.startsWith("/supply")) return "supply";
    if (pathname.startsWith("/tariffs")) return "tariffs";
    if (pathname.startsWith("/shipments")) return "shipments";
    if (pathname.startsWith("/sneaker/")) {
      const fromPage =
        location.state?.fromPage ||
        new URLSearchParams(location.search).get("from");
      return fromPage === "sales" ? "sales" : "inventory";
    }
    return "";
  };

  const currentActivePage =
    activePage || getActivePageFromPath(location.pathname);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleNotificationsClick = () => {
    openModal();
  };

  // Загружаем непрочитанные уведомления при монтировании компонента
  useEffect(() => {
    fetchUnreadNotifications();
  }, [fetchUnreadNotifications]);

  // Блокируем скролл при открытом меню на мобильных
  useEffect(() => {
    if (isVisible && window.innerWidth <= 768) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isVisible]);

  return (
    <SidebarContainer $isVisible={isVisible}>
      <MobileCloseButton onClick={onClose}>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6 6L18 18"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </MobileCloseButton>

      <ContentWrapper>
        <TopSection>
          <LogoContainer>
            <LogoLink href="https://legitfam.pro/" target="_blank" rel="noopener noreferrer">
              <Logo size="medium" />
            </LogoLink>
          </LogoContainer>
          <Divider />
        </TopSection>

        <NavSection>
          <NavMenu>
            <NavItem
              to="/profile"
              className={currentActivePage === "profile" ? "active" : ""}
              onClick={onClose}
            >
              <IconWrapper>
                <ProfileIcon isActive={currentActivePage === "profile"} />
              </IconWrapper>
              <NavItemText>Профиль</NavItemText>
            </NavItem>

            <NavItem
              to="/inventory"
              className={currentActivePage === "inventory" ? "active" : ""}
              onClick={onClose}
              data-tutorial="inventory-nav"
            >
              <IconWrapper>
                <InventoryIcon isActive={currentActivePage === "inventory"} />
              </IconWrapper>
              <NavItemText>Инвентарь</NavItemText>
            </NavItem>

            <NavItem
              to="/shipments"
              className={currentActivePage === "shipments" ? "active" : ""}
              onClick={onClose}
            >
              <IconWrapper>
                <ShipmentsIcon isActive={currentActivePage === "shipments"} />
              </IconWrapper>
              <NavItemText>Отгрузки</NavItemText>
            </NavItem>

            <NavItem
              to="/sales"
              className={currentActivePage === "sales" ? "active" : ""}
              onClick={onClose}
            >
              <IconWrapper>
                <SalesIcon isActive={currentActivePage === "sales"} />
              </IconWrapper>
              <NavItemText>Продажи</NavItemText>
            </NavItem>

            <NavItem
              to="/statistics"
              className={currentActivePage === "statistics" ? "active" : ""}
              onClick={onClose}
            >
              <IconWrapper>
                <StatsIcon isActive={currentActivePage === "statistics"} />
              </IconWrapper>
              <NavItemText>Статистика</NavItemText>
            </NavItem>

            <NavItem
              to="/search"
              className={currentActivePage === "search" ? "active" : ""}
              onClick={onClose}
            >
              <IconWrapper>
                <SearchIcon isActive={currentActivePage === "search"} />
              </IconWrapper>
              <NavItemText>Search</NavItemText>
            </NavItem>

            <NavItem
              to="/supply"
              className={currentActivePage === "supply" ? "active" : ""}
              onClick={onClose}
            >
              <IconWrapper>
                <SupplyIcon isActive={currentActivePage === "supply"} />
              </IconWrapper>
              <NavItemText>Закупки</NavItemText>
            </NavItem>

            <NavItem
              to="/tariffs"
              className={currentActivePage === "tariffs" ? "active" : ""}
              onClick={onClose}
            >
              <IconWrapper>
                <PlansIcon isActive={currentActivePage === "tariffs"} />
              </IconWrapper>
              <NavItemText>Тарифы</NavItemText>
            </NavItem>
          </NavMenu>
        </NavSection>

        <BottomSection>
          <NotificationButton onClick={handleNotificationsClick}>
            <IconWrapper>
              <NotificationIcon hasUnread={unreadCount > 0} />
            </IconWrapper>
            <NavItemText style={{ color: unreadCount > 0 ? "#24B97D" : "#609F94" }}>
              Уведомления
            </NavItemText>
            {unreadCount > 0 && (
              <NotificationBadge>
                {unreadCount > 99 ? '99+' : unreadCount}
              </NotificationBadge>
            )}
          </NotificationButton>
          
          <LogoutButton onClick={handleLogout}>
            <IconWrapper>
              <LogoutIcon />
            </IconWrapper>
            <NavItemText style={{ color: "#D85355" }}>Выйти</NavItemText>
          </LogoutButton>
        </BottomSection>
      </ContentWrapper>
      
      <NotificationsModal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
      />
    </SidebarContainer>
  );
};

export default Sidebar;
